import { collection, doc, getDoc, getDocs } from 'firebase/firestore';
import { db } from '../config/firebase';
import { isTeamPlanet, parsePlayerPremium, type BuildingType, type Planet, type PlayerPremium, type PlayerRace, type ResourceMultiplier, type ShipId, type TradeableResource } from '@kaeliux/shared';
import { getSelectedUniverseId } from '../store/universeStore';
import { HEARTBEAT_MS } from '../config/gameLoop';
import { authedPost, requestTransitionCount } from './http';
import { normalizePlanet } from './planetNormalization';

/**
 * Universe context accepted (but ignored) by every gameplay call — the real
 * backend fetches its own universe doc server-side. Mock mode has no
 * server, so its equivalent functions actually consume these fields; the
 * shape is kept identical here so callers (ZonePage) can pass one options
 * object regardless of which service implementation is aliased in.
 */
export interface PlanetActionOptions {
    resourceMultiplier: ResourceMultiplier;
    sunAges: Record<string, number[]>;
    speedMultiplier?: number;
}

export async function getPlanet(userId: string, planetId: string): Promise<Planet | null> {
    const ref = doc(db, 'universes', getSelectedUniverseId(), 'players', userId, 'planets', planetId);
    const snap = await getDoc(ref);
    if (!snap.exists()) return null;
    return normalizePlanet(snap.data(), planetId);
}

/**
 * Every planet the player owns — the source for the top-nav planet switcher.
 * Read directly via the client SDK: the security rules make a player's own
 * `players/{userId}/**` subtree owner-readable (and thus listable), so no
 * Cloud Function is needed. Sorted by creation so the homeworld leads.
 *
 * A converted Team planet physically lives in this SAME subcollection (see
 * `isTeamPlanet`'s doc comment) but is a shared alliance asset, not one of
 * this player's own — excluded here so it never pollutes the switcher, the
 * colonize-limit count, or any other "my planets" read.
 */
export async function listPlanets(userId: string): Promise<Planet[]> {
    const col = collection(db, 'universes', getSelectedUniverseId(), 'players', userId, 'planets');
    const snap = await getDocs(col);
    return snap.docs
        .map((d) => normalizePlanet(d.data(), d.id))
        .filter((p) => !isTeamPlanet(p))
        .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
}

export async function createInitialPlanet(_userId: string, race: PlayerRace): Promise<string> {
    const data = await authedPost<{ planetId: string }>('/createPlanet', { race });
    return data.planetId;
}

const ticksInFlight = new Map<string, { tick: Promise<Planet>; since: number; startedAt: number }>();

/**
 * Under the game loop's heartbeat, so a hung tick is never handed to the next
 * beat. Its own callers (the tick spinner, the due-scan's planet latch) still
 * wait on it until `REQUEST_TIMEOUT_MS` aborts it. The price: a tick that is
 * slow rather than hung gets a second concurrent request on the same planet
 * once it passes this window — the lock contention issue #165 removed — which
 * is acceptable at half a minute.
 */
const TICK_SHARE_WINDOW_MS = HEARTBEAT_MS / 2;

/**
 * Concurrent ticks of the same planet share one request. The game loop's
 * heartbeat, its due-scan and the science/ship-upgrade refreshes each tick
 * independently, and separate requests would be separate server transactions
 * on the same planet and player docs, queueing on each other's locks
 * (issue #165).
 *
 * A tick is only shared while no request other than a tick has started or
 * landed since it began: a caller ticking to pick up its own mutation (a
 * rename, say) must not be handed a result the server computed before that
 * mutation committed. Nor is a tick shared once it has been in flight for
 * `TICK_SHARE_WINDOW_MS`: an idle heartbeat moves no counter, so a request
 * that hangs would otherwise be handed to every later tick until it times
 * out (`REQUEST_TIMEOUT_MS`, longer than a heartbeat).
 */
export function tickPlanet(planetId: string, _options?: PlanetActionOptions): Promise<Planet> {
    const inFlight = ticksInFlight.get(planetId);
    if (inFlight && inFlight.since === requestTransitionCount() && Date.now() - inFlight.startedAt < TICK_SHARE_WINDOW_MS) {
        return inFlight.tick;
    }

    const tick: Promise<Planet> = authedPost<unknown>('/tickPlanet', { planetId }, { replayOnly: true })
        .then((data) => normalizePlanet(data, planetId))
        .finally(() => {
            if (ticksInFlight.get(planetId)?.tick === tick) ticksInFlight.delete(planetId);
        });
    ticksInFlight.set(planetId, { tick, since: requestTransitionCount(), startedAt: Date.now() });
    return tick;
}

export async function upgradeBuilding(
    planetId: string,
    buildingType: BuildingType,
    _options?: PlanetActionOptions,
    /**
     * Chosen plot (DS-28) — only meaningful for a level 0→1 construct; the
     * server validates it and ignores it for an upgrade of an already-built
     * building.
     */
    plotId?: string,
): Promise<Planet> {
    const data = await authedPost<unknown>('/upgradeBuilding', { planetId, buildingType, plotId });
    return normalizePlanet(data, planetId);
}

/**
 * Platinum "Queue max" (issue #219): queue as many consecutive levels of one
 * building as fit, in one server transaction. The server decides how many.
 */
export async function queueMaxBuildingLevels(
    planetId: string,
    buildingType: BuildingType,
    _options?: PlanetActionOptions,
    /** Only meaningful when the first queued level is a level 0→1 construct. */
    plotId?: string,
    /** The count the player was shown — the server never queues more. */
    maxLevels?: number,
): Promise<{ planet: Planet; queuedCount: number }> {
    const data = await authedPost<Record<string, unknown>>('/queueMaxBuildingLevels', { planetId, buildingType, plotId, maxLevels });
    return {
        planet: normalizePlanet(data, planetId),
        queuedCount: typeof data.queuedCount === 'number' ? data.queuedCount : 0,
    };
}

export async function cancelBuildingUpgrade(
    planetId: string,
    eventId: number,
    _options?: PlanetActionOptions,
): Promise<Planet> {
    const data = await authedPost<unknown>('/cancelBuildingUpgrade', { planetId, eventId });
    return normalizePlanet(data, planetId);
}

/** Pay Dark Matter to instantly complete a pending buildingBuild event. */
export async function rushBuildingUpgrade(
    planetId: string,
    eventId: number,
    _options?: PlanetActionOptions,
): Promise<{ planet: Planet; premium: PlayerPremium }> {
    const data = await authedPost<Record<string, unknown>>('/rushBuildingUpgrade', { planetId, eventId });
    return {
        planet: normalizePlanet(data, planetId),
        premium: parsePlayerPremium(data.premium),
    };
}

export async function destroyBuilding(
    planetId: string,
    buildingType: BuildingType,
    _options?: PlanetActionOptions,
): Promise<Planet> {
    const data = await authedPost<unknown>('/destroyBuilding', { planetId, buildingType });
    return normalizePlanet(data, planetId);
}

export async function buildShips(
    planetId: string,
    shipId: ShipId,
    quantity: number,
    _options?: PlanetActionOptions,
): Promise<Planet> {
    const data = await authedPost<unknown>('/buildShips', { planetId, shipId, quantity });
    return normalizePlanet(data, planetId);
}

export async function cancelShipBuild(
    planetId: string,
    eventId: number,
    _options?: PlanetActionOptions,
): Promise<Planet> {
    const data = await authedPost<unknown>('/cancelShipBuild', { planetId, eventId });
    return normalizePlanet(data, planetId);
}

/** Pay Dark Matter to instantly complete a pending shipBuild batch (not available for a Robots delivery group). */
export async function rushShipBuild(
    planetId: string,
    eventId: number,
    _options?: PlanetActionOptions,
): Promise<{ planet: Planet; premium: PlayerPremium }> {
    const data = await authedPost<Record<string, unknown>>('/rushShipBuild', { planetId, eventId });
    return {
        planet: normalizePlanet(data, planetId),
        premium: parsePlayerPremium(data.premium),
    };
}

/**
 * Robots-only (issue #91): queue multiple ship-build orders as one linked
 * group whose combined delivery is held back automatically for
 * AUTO_DELIVERY_HOLD_HOURS after the group's construction completes,
 * instead of docking automatically at each item's own endDate. Delivery can
 * also be requested on demand any time from completion onward — see
 * {@link deliverShipGroup}.
 */
export async function buildShipGroup(
    planetId: string,
    items: Partial<Record<ShipId, number>>,
    _options?: PlanetActionOptions,
): Promise<Planet> {
    const data = await authedPost<unknown>('/buildShipGroup', { planetId, items });
    return normalizePlanet(data, planetId);
}

/**
 * Deliver a pending Robots ship-build group on demand, before its automatic
 * AUTO_DELIVERY_HOLD_HOURS deadline (issue #91) — `groupId` is the shared id
 * every batch in the group carries.
 */
export async function deliverShipGroup(
    planetId: string,
    groupId: number,
    _options?: PlanetActionOptions,
): Promise<Planet> {
    const data = await authedPost<unknown>('/deliverShipGroup', { planetId, groupId });
    return normalizePlanet(data, planetId);
}

export async function scrapShips(
    planetId: string,
    ships: Partial<Record<ShipId, number>>,
    _options?: PlanetActionOptions,
): Promise<Planet> {
    const data = await authedPost<unknown>('/scrapShips', { planetId, ships });
    return normalizePlanet(data, planetId);
}

export async function startRecycle(
    planetId: string,
    ships: Partial<Record<ShipId, number>>,
    _options?: PlanetActionOptions,
): Promise<Planet> {
    const data = await authedPost<unknown>('/startRecycle', { planetId, ships });
    return normalizePlanet(data, planetId);
}

export async function cancelRecycle(
    planetId: string,
    eventId: number,
    _options?: PlanetActionOptions,
): Promise<Planet> {
    const data = await authedPost<unknown>('/cancelRecycle', { planetId, eventId });
    return normalizePlanet(data, planetId);
}

/**
 * Pay Dark Matter to instantly finish a pending scrapRecycle batch's
 * processing. The automatic AUTO_DELIVERY_HOLD_HOURS delivery hold still
 * applies afterward, shifted to start from the new (earlier) completion —
 * {@link deliverRecycle} can still be called once it's ready to skip that too.
 */
export async function rushRecycle(
    planetId: string,
    eventId: number,
    _options?: PlanetActionOptions,
): Promise<{ planet: Planet; premium: PlayerPremium }> {
    const data = await authedPost<Record<string, unknown>>('/rushRecycle', { planetId, eventId });
    return {
        planet: normalizePlanet(data, planetId),
        premium: parsePlayerPremium(data.premium),
    };
}

/**
 * Deliver a pending scrapRecycle batch on demand, before its automatic
 * AUTO_DELIVERY_HOLD_HOURS deadline (issue #21's 48-hour hold, every race).
 */
export async function deliverRecycle(
    planetId: string,
    eventId: number,
    _options?: PlanetActionOptions,
): Promise<Planet> {
    const data = await authedPost<unknown>('/deliverRecycle', { planetId, eventId });
    return normalizePlanet(data, planetId);
}

/**
 * Rename a planet and/or assign a preset image (`planetType`, 1-indexed
 * into the free preset set — see `FREE_PLANET_IMAGE_COUNT`). Returns void
 * rather than a `Planet` — the response is only the two changed fields, not
 * a full planet doc — so callers should refresh planet state themselves
 * (e.g. via `planetStore.tickCurrentPlanet()`) after this resolves.
 */
export async function updatePlanet(
    planetId: string,
    updates: { name?: string; planetType?: number },
): Promise<void> {
    await authedPost<unknown>('/updatePlanet', { planetId, ...updates });
}

/**
 * Permanently delete a non-main planet (issue #28). The server rejects the
 * main planet, an active 24h cooldown, incoming fleets, or outbound
 * espionage fleets originating from this planet with a specific
 * failed-precondition message — surface that message to the user rather
 * than a generic failure.
 */
export async function deletePlanet(planetId: string): Promise<void> {
    await authedPost<unknown>('/deletePlanet', { planetId });
}

/**
 * Exchange `amount` of `from` for `to` at the Market's sell/buy
 * spread. Gated server-side by the Market's global 6-hour trade
 * window (independent of this planet's own history — see
 * `packages/game-engine/src/trade.ts`) and the player's trade-token
 * balance, which this call debits but does NOT return — refresh it via
 * `usePlayerStore().loadPlayerState(...)` after a successful trade.
 */
export async function tradeResources(
    planetId: string,
    from: TradeableResource,
    to: TradeableResource,
    amount: number,
    _options?: PlanetActionOptions,
): Promise<{ planet: Planet; premium: PlayerPremium }> {
    const data = await authedPost<Record<string, unknown>>('/tradeResources', { planetId, from, to, amount });
    return {
        planet: normalizePlanet(data, planetId),
        premium: parsePlayerPremium(data.premium),
    };
}
